import * as React from 'react';
import { compose } from 'redux';
import { withUserWebView } from '@shopee-user/with-web-view';
import { AuthMethod } from '@shopee/web-bridge-sdk';

// Components
import Divider from '../../../components/Divider';
import ConnectWithShopee from './components/ConnectWithShopee';
import ConnectWithGoogle from './components/ConnectWithGoogle';
import ConnectWithFacebook from './components/ConnectWithFacebook';
import ConnectWithApple from './components/ConnectWithApple';

// HOC
import { withThirdPartyLoginFlowEvent } from './flow-event';
import withToast from '@tob/shared/components/Toast/hoc';

// Hooks
import { useResponsiveLayout } from '@shopee-user/layout-responsive';
import { isWebBridgeSupported, useTranslation } from '@shopee-user/platform';

// Constants & Enums
import { Layout as ResponsiveLayout } from '@shopee-user/layout-responsive';
import { AuthenticateChannel } from '@tob/shared/thirdparty-auth/core/types';
import {
  ThirdpartyBindingEntryPoint,
  ThirdpartyAuthOperation,
} from '../../service/constants';
import styles from './style.scss';
import { CLIENT_ID_TO_REDIRECTION_CONFIG } from '@shopee-user/authentication-redirect';

// Utils
import cx from 'classnames';
import {
  isSupportFacebookOAuth,
  isSupportGoogleOAuth,
  isSupportShopeeOAuth,
  isSupportAppleOAuth,
} from '../../utils';
import { navigateToThirdPartyAuthService } from '../../../../pages/third-party-service/sessionStorage';
import { useGetErrorMessage } from '@shopee-user/get-error-message';
import logInNonLive from '@tob/shared/utils/log-in-nonlive';
import { useQuickLoginContext } from '@tob/shared/quick-login/context';
// Types
import type { WithUserWebViewProps } from '@shopee-user/with-web-view';
import { parseQueryStringToObj } from '@shopee/url-util';
import { isValidUrl } from '@tob/shared/redirect-utils/util';
import { AuthenticationConfig } from '@tob/shared/redirect-utils/types';
import { WithToastProps } from '@tob/shared/components/Toast/types';
import { ErrorCode } from '@tob/shared/api-constants';
export type Layout = 'vertical' | 'horizontal';

type InnerProps = {
  /**
   * Layout of the login options
   * @description If not provided, it will be determined by the responsive layout:
   * - 'vertical' for mobile
   * - 'horizontal' for desktop
   * @default 'vertical'
   */
  layout?: Layout;
  className?: string;
  dividerClassName?: string;
  entryPoint: ThirdpartyBindingEntryPoint;
};

type InnerThirdpartyLoginMethodsProps = InnerProps &
  WithUserWebViewProps &
  WithToastProps;

function InnerThirdpartyLoginMethods({
  layout: layoutFromProps,
  className,
  showToast,
  dividerClassName,
  webViewGetAuthConfig,
  webViewGetAvailableToBAuthMethods,
  webViewAuthWith3PServiceByToken,
  entryPoint,
}: InnerThirdpartyLoginMethodsProps) {
  const isInApp = isWebBridgeSupported();
  const [
    supportedAuthMethodsInApp,
    setSupportedAuthMethodsInApp,
  ] = React.useState<AuthMethod[] | null>(isInApp ? null : []);

  const {
    state: { isQuickLoginFeatureEnabled, toggleValue },
  } = useQuickLoginContext();
  const doesUserIntendToActivateQuickLogin =
    isQuickLoginFeatureEnabled && toggleValue;

  React.useEffect(() => {
    if (!isInApp) return;

    let cancelled = false;
    (async () => {
      try {
        const response = await webViewGetAvailableToBAuthMethods({
          methods: [
            AuthMethod.Google,
            AuthMethod.Facebook,
            AuthMethod.Apple,
            AuthMethod.ShopeeOAuth,
          ],
        });
        if (!cancelled) {
          setSupportedAuthMethodsInApp(
            response.errorCode === 0
              ? response.availableToBAuthMethods ?? []
              : []
          );
        }
      } catch (error) {
        logInNonLive('getAvailableAuthMethods error:', error);
        if (!cancelled) {
          setSupportedAuthMethodsInApp([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isInApp]);

  const { layout: responsiveLayout } = useResponsiveLayout();
  const i18n = useTranslation();
  const { getErrorMessage } = useGetErrorMessage();
  const isHorizontalLayout = React.useMemo(() => {
    if (layoutFromProps) {
      return layoutFromProps === 'horizontal';
    }
    return responsiveLayout === ResponsiveLayout.Desktop;
  }, [layoutFromProps, responsiveLayout]);

  const optionLayout = isHorizontalLayout ? 'icon_button' : 'text_button';

  const triggerUrl = new URL(window.location.href);
  const { next: loginNextUrl, client_id } = parseQueryStringToObj(
    triggerUrl.search
  );
  // Validate next URL
  const isValidLoginNextUrl =
    isValidUrl({
      url: loginNextUrl,
      authenticationConfig: (CLIENT_ID_TO_REDIRECTION_CONFIG[
        client_id
      ] as unknown) as AuthenticationConfig,
    }) || isWebBridgeSupported();

  const onHandleShopeeAuthentication = async ({
    authCode,
    appClientId,
  }: {
    authCode?: string;
    appClientId?: string;
  } = {}) => {
    if (!isValidLoginNextUrl) {
      showToast({
        text: getErrorMessage({
          error: ErrorCode.FeErrorCode,
          error_msg: 'Invalid next URL',
        }),
        level: 'warning',
      });
      return;
    }

    if (!appClientId) {
      showToast({
        text: getErrorMessage({
          error: ErrorCode.FeErrorCode,
          error_msg: 'Empty App Client ID',
        }),
        level: 'warning',
      });
      return;
    }

    navigateToThirdPartyAuthService({
      context: {
        channel: AuthenticateChannel.Shopee,
        client_id: appClientId,
        entry_point: entryPoint,
        operation: ThirdpartyAuthOperation.Login,
        redirect_uri: loginNextUrl,
        authCode: authCode,
      },
      shouldReplaceCurrentUrl: false,
      doesUserIntendToActivateQuickLogin,
    });
  };

  const onHandleGoogleAuthentication = async ({
    authCode,
    appClientId,
  }: {
    authCode?: string;
    appClientId?: string;
  } = {}) => {
    if (!isValidLoginNextUrl) {
      showToast({
        text: getErrorMessage({
          error: ErrorCode.FeErrorCode,
          error_msg: 'Invalid next URL',
        }),
        level: 'warning',
      });
      return;
    }

    if (!appClientId) {
      showToast({
        text: getErrorMessage({
          error: ErrorCode.FeErrorCode,
          error_msg: 'Empty App Client ID',
        }),
        level: 'warning',
      });
      return;
    }

    navigateToThirdPartyAuthService({
      context: {
        channel: AuthenticateChannel.Google,
        client_id: appClientId,
        entry_point: entryPoint,
        operation: ThirdpartyAuthOperation.Login,
        redirect_uri: loginNextUrl,
        authCode: authCode,
      },
      shouldReplaceCurrentUrl: false,
      doesUserIntendToActivateQuickLogin,
    });
  };

  const onHandleFacebookAuthentication = async ({
    authCode,
    appClientId,
  }: {
    authCode?: string;
    appClientId?: string;
  } = {}) => {
    if (!isValidLoginNextUrl) {
      showToast({
        text: getErrorMessage({
          error: ErrorCode.FeErrorCode,
          error_msg: 'Invalid next URL',
        }),
        level: 'warning',
      });
      return;
    }

    if (!appClientId) {
      showToast({
        text: getErrorMessage({
          error: ErrorCode.FeErrorCode,
          error_msg: 'Empty App Client ID',
        }),
        level: 'warning',
      });
      return;
    }

    navigateToThirdPartyAuthService({
      context: {
        channel: AuthenticateChannel.Facebook,
        client_id: appClientId,
        entry_point: entryPoint,
        operation: ThirdpartyAuthOperation.Login,
        redirect_uri: loginNextUrl,
        authCode: authCode,
      },
      shouldReplaceCurrentUrl: false,
      doesUserIntendToActivateQuickLogin,
    });
  };

  const onHandleAppleAuthentication = async ({
    idToken,
    appClientId,
  }: {
    idToken?: string;
    appClientId?: string;
  } = {}) => {
    if (!isValidLoginNextUrl) {
      showToast({
        text: getErrorMessage({
          error: ErrorCode.FeErrorCode,
          error_msg: 'Invalid next URL',
        }),
        level: 'warning',
      });
      return;
    }

    if (!appClientId) {
      showToast({
        text: getErrorMessage({
          error: ErrorCode.FeErrorCode,
          error_msg: 'Empty App Client ID',
        }),
        level: 'warning',
      });
      return;
    }

    navigateToThirdPartyAuthService({
      context: {
        channel: AuthenticateChannel.Apple,
        client_id: appClientId,
        entry_point: entryPoint,
        operation: ThirdpartyAuthOperation.Login,
        redirect_uri: loginNextUrl,
        idToken: idToken,
      },
      shouldReplaceCurrentUrl: false,
      doesUserIntendToActivateQuickLogin,
    });
  };

  const isMethodAvailableInApp = (method: AuthMethod) =>
    supportedAuthMethodsInApp?.includes(method) ?? false;

  const showGoogle = isInApp
    ? isSupportGoogleOAuth() && isMethodAvailableInApp(AuthMethod.Google)
    : isSupportGoogleOAuth();
  const showFacebook = isInApp
    ? isSupportFacebookOAuth() && isMethodAvailableInApp(AuthMethod.Facebook)
    : isSupportFacebookOAuth();
  const showApple = isInApp
    ? isSupportAppleOAuth() && isMethodAvailableInApp(AuthMethod.Apple)
    : isSupportAppleOAuth();
  const showShopee = isInApp
    ? isSupportShopeeOAuth() && isMethodAvailableInApp(AuthMethod.ShopeeOAuth)
    : isSupportShopeeOAuth();

  const hasAnyLoginMethod =
    showShopee || showGoogle || showFacebook || showApple;

  if (supportedAuthMethodsInApp === null || !hasAnyLoginMethod) {
    return null;
  }

  return (
    <div className={cx(styles.container, className)}>
      <Divider
        label={i18n.t('label_auth_divider_or')}
        lineMode={isHorizontalLayout ? 'solid' : 'gradient'}
        className={dividerClassName}
      />
      <div
        className={cx(
          styles.list,
          isHorizontalLayout ? styles.horizontal : styles.vertical
        )}
      >
        {showShopee && (
          <ConnectWithShopee
            className={
              isHorizontalLayout
                ? styles.horizontalOptionItem
                : styles.verticalOptionItem
            }
            label={i18n.t(
              entryPoint === ThirdpartyBindingEntryPoint.SignupByOtpPage
                ? 'label_auth_thirdparty_signup_with_shopee'
                : 'label_auth_thirdparty_login_with_shopee'
            )}
            layout={optionLayout}
            onAuthenticated={({ authCode, appClientId }) =>
              onHandleShopeeAuthentication({ authCode, appClientId })
            }
            onError={() => {
              showToast({
                text: getErrorMessage({
                  error: ErrorCode.FeErrorCode,
                  error_msg:
                    '[3P - Shopee] Error occurred while authenticating',
                }),
                level: 'warning',
              });
              return;
            }}
            onCancel={() => {
              showToast({
                text: getErrorMessage({
                  error: ErrorCode.FeErrorCode,
                  error_msg: '[3P - Shopee] Authentication cancelled',
                }),
                level: 'warning',
              });
            }}
            webViewGetAuthConfig={webViewGetAuthConfig}
            entryPoint={entryPoint}
          />
        )}
        {showGoogle && (
          <ConnectWithGoogle
            className={
              isHorizontalLayout
                ? styles.horizontalOptionItem
                : styles.verticalOptionItem
            }
            entryPoint={entryPoint}
            label={i18n.t(
              entryPoint === ThirdpartyBindingEntryPoint.SignupByOtpPage
                ? 'label_auth_signup_with_google'
                : 'label_auth_login_with_google'
            )}
            layout={optionLayout}
            webViewAuthWith3PServiceByToken={webViewAuthWith3PServiceByToken}
            onAuthenticated={({ authCode, appClientId }) =>
              onHandleGoogleAuthentication({ authCode, appClientId })
            }
            onError={() => {
              showToast({
                text: getErrorMessage({
                  error: ErrorCode.FeErrorCode,
                  error_msg:
                    '[3P - Google] Error occurred while authenticating',
                }),
                level: 'warning',
              });
              return;
            }}
            onCancel={() => {
              showToast({
                text: getErrorMessage({
                  error: ErrorCode.FeErrorCode,
                  error_msg: '[3P - Google] Authentication cancelled',
                }),
                level: 'warning',
              });
              return;
            }}
          />
        )}
        {showFacebook && (
          <ConnectWithFacebook
            className={
              isHorizontalLayout
                ? styles.horizontalOptionItem
                : styles.verticalOptionItem
            }
            label={i18n.t(
              entryPoint === ThirdpartyBindingEntryPoint.SignupByOtpPage
                ? 'label_auth_signup_with_facebook'
                : 'label_auth_login_with_facebook'
            )}
            entryPoint={entryPoint}
            layout={optionLayout}
            webViewAuthWith3PServiceByToken={webViewAuthWith3PServiceByToken}
            onAuthenticated={({ authCode, appClientId }) =>
              onHandleFacebookAuthentication({ authCode, appClientId })
            }
            onError={() => {
              showToast({
                text: getErrorMessage({
                  error: ErrorCode.FeErrorCode,
                  error_msg:
                    '[3P - Facebook] Error occurred while authenticating',
                }),
                level: 'warning',
              });
              return;
            }}
            onCancel={() => {
              showToast({
                text: getErrorMessage({
                  error: ErrorCode.FeErrorCode,
                  error_msg: '[3P - Facebook] Authentication cancelled',
                }),
                level: 'warning',
              });
              return;
            }}
          />
        )}
        {showApple && (
          <ConnectWithApple
            className={
              isHorizontalLayout
                ? styles.horizontalOptionItem
                : styles.verticalOptionItem
            }
            label={i18n.t(
              entryPoint === ThirdpartyBindingEntryPoint.SignupByOtpPage
                ? 'label_auth_signup_with_apple'
                : 'label_auth_login_with_apple'
            )}
            entryPoint={entryPoint}
            layout={optionLayout}
            webViewAuthWith3PServiceByToken={webViewAuthWith3PServiceByToken}
            onAuthenticated={({ idToken, appClientId }) =>
              onHandleAppleAuthentication({ idToken, appClientId })
            }
            onError={() => {
              showToast({
                text: getErrorMessage({
                  error: ErrorCode.FeErrorCode,
                  error_msg: '[3P - Apple] Error occurred while authenticating',
                }),
                level: 'warning',
              });
              return;
            }}
            onCancel={() => {
              showToast({
                text: getErrorMessage({
                  error: ErrorCode.FeErrorCode,
                  error_msg: '[3P - Apple] Authentication cancelled',
                }),
                level: 'warning',
              });
              return;
            }}
          />
        )}
      </div>
    </div>
  );
}

const InnerThirdpartyLoginMethodsWithWebview = compose<
  React.ComponentType<InnerProps>
>(
  withUserWebView({}),
  withToast
)(InnerThirdpartyLoginMethods);

export type ThirdpartyLoginMethodsProps = InnerProps;

function ThirdpartyLoginMethods({
  ...innerProps
}: ThirdpartyLoginMethodsProps) {
  return <InnerThirdpartyLoginMethodsWithWebview {...innerProps} />;
}

export default withThirdPartyLoginFlowEvent<ThirdpartyLoginMethodsProps>(
  ThirdpartyLoginMethods
);
